import { useEffect, useState } from "react";
import { accessRequest } from "@/api/tools";
import { useWrite } from "@/api/useTool";
import { Field, ReasonDialog, Receipt } from "@/ui";
import { useSession } from "@/session/useSession";
import { useViewState } from "@/shell/viewState";
import { V1Slot } from "@/shell/island";

// V1-INTERACTIONS §3.3: the one screen an unknown id, a malformed route, an archived record the actor may not read, and an
// id that exists but the actor may not open all resolve to. Same body for every case; never a substitute record (Law 15).
// A V1 screen: inside a V2 view's island it renders through the V1 slot, outside `.mc` (shell/island.tsx).
export function NotFound({ route }: { route: string }) {
  return <V1Slot><NotFoundBody route={route} /></V1Slot>;
}

function NotFoundBody({ route }: { route: string }) {
  const { register } = useViewState();
  const { me, can } = useSession();
  const [asking, setAsking] = useState(false);
  const [scope, setScope] = useState<"read" | "cover">("read");
  const ask = useWrite(accessRequest, [["today"], ["action-center"], ["home"]]);
  useEffect(() => register({ view: "not-found", ids: { route }, summary: "not found" }), [register, route]);
  const m = /^requisitions\/([^/]+)/.exec(route);
  const requisitionId = m?.[1] && /^R-\d+$/.test(m[1]) ? m[1] : null;
  const offerAccess = !!requisitionId && !!me?.roles.includes("recruiter") && can("access.request");
  return (
    <div>
      <h1 className="page">We can't show that page</h1>
      <p className="lede">The link may be wrong, the record may have moved, or it may not be yours to open.</p>
      <p className="muted mono" style={{ fontSize: "var(--fs-sm)" }}>
        #{route}
      </p>
      <div className="row" style={{ gap: 8, marginTop: 12 }}>
        {/* BRD 4.5 / home.md §1.1: Home is the first destination for every persona; "Today" is GLOSSARY §22. */}
        <a className="btn btn--primary" href="#home">
          Go to Home
        </a>
        <button type="button" className="btn" onClick={() => window.history.back()}>
          Back
        </button>
        {offerAccess && (
          <button type="button" className="btn" onClick={() => setAsking(true)}>
            Request access
          </button>
        )}
      </div>
      {offerAccess && ask.isSuccess && <Receipt>Requested — the owner has it in the Action Center.</Receipt>}
      {offerAccess && <ReasonDialog title="Ask for access" label="Why you need it" confirm="Ask" open={asking} onClose={() => setAsking(false)} busy={ask.isPending} error={ask.error} onConfirm={(reason) => ask.mutate({ requisition_id: requisitionId!, scope, reason }, { onSuccess: () => setAsking(false) })}>
        <Field label="Access scope" id="not-found-access-scope"><select id="not-found-access-scope" value={scope} onChange={(event) => setScope(event.target.value as "read" | "cover")}><option value="read">Read — no writes</option><option value="cover">Cover — permitted owner actions</option></select></Field>
        <p className="muted">The owner or Admin decides the scope and duration. Cover excludes granting access, closing, reopening, reassigning seats and exporting the Summary Sheet.</p>
      </ReasonDialog>}
    </div>
  );
}
